<template>
  <section v-if="isEditMode || isEmpty" class="section">
    <section-title>
      <template slot="title">{{$t(areFiltersApplied ? 'CONTENT_FILTERED_RESULTS_LABEL' : 'CONTENT_ALL_HEADER')}}</template>
    </section-title>

    <null-state-section
      v-if="isEditMode || isEmpty"
      :reverse="true"
      :bodyText="$t('CONTENT_FILTER_EMPTY_HEADER')"
      :header="$t('CONTENT_FILTER_EMPTY_SUPPORTING')"
      image="search"
    >
      <sh-button
        color="primary"
        size="medium"
        @click="resetFilters"
      >
        {{ $t('CONTENT_FILTER_RESET_DEFAULTS_CTA') }}
      </sh-button>
    </null-state-section>
  </section>

  <section v-else class="all-content section">
    <section-title>
      <template slot="title">{{$t(areFiltersApplied ? 'CONTENT_FILTERED_RESULTS_LABEL' : 'CONTENT_ALL_HEADER')}}</template>
    </section-title>
    <card-group
      type="all"
      cardStyle="house-note"
      :cards="allHouseNoteCards"
      role="list"
    />
    <dh-loading-indicator v-if="allFetching" />
    <div class="load-more" v-if="!allNotesFetched && allHouseNoteCards.length > 0">
      <sh-button
        :disabled="allFetching"
        :loading="allFetching"
        :aria-label="`${$t('CONTENT_LOAD_MORE_CTA')}`"
        role="button"
        @click="fetchMoreNotes"
      >
        {{ $t('CONTENT_LOAD_MORE_CTA') }}
        </sh-button>
    </div>
  </section>
</template>
<script>

import { createNamespacedHelpers } from 'vuex'
import { transformHouseNotesToCards } from '~/helpers'
import UIMixin from '~/mixins/ui'

import NoteFilterForm from '~/components/note-filter-form'
import SectionTitle from '~/components/section-title'
import NullStateSection from '../null-state-section'
import CardGroup from '../card-group'

const { mapActions: mapNoteActions, mapGetters: mapNoteGetters } = createNamespacedHelpers('content/notes')
const { mapGetters: mapUserGetters } = createNamespacedHelpers('user')
const { mapGetters: mapJssGetters } = createNamespacedHelpers('jss')
const { mapGetters: mapUserAccountGetters } = createNamespacedHelpers('user/account')

export default {
  name: 'FilteredNotes',

  components: {
    CardGroup,
    NoteFilterForm,
    NullStateSection,
    SectionTitle
  },

  props: {
    rendering: {
      type: Object
    }
  },

  mixins: [
    UIMixin
  ],

  computed: {
    ...mapUserGetters(['user']),
    ...mapUserAccountGetters(['favoriteContentCategories']),

    ...mapNoteGetters([
      'allNotes',
      'allFetching',
      'allFailed',
      'allNotesFetched',
      'areFiltersApplied'
    ]),

    ...mapJssGetters([
      'isEditMode'
    ]),

    allHouseNoteCards () {
      return transformHouseNotesToCards(this.allNotes, this.localePath)
    },

    isEmpty () {
      return !this.allFetching && this.allHouseNoteCards.length === 0
    },

    query () {
      return this.$route.query
    }
  },

  watch: {
    query () {
      const { query } = this
      this.rehydrateNoteFilters({ query })
    }
  },

  mounted () {
    if (!this.isEditMode) {
      const { query } = this
      this.rehydrateNoteFilters({ query })

      this.fetchNotes()
    }
  },

  beforeDestroy () {
    this.resetNoteFilters()
  },

  methods: {
    ...mapNoteActions([
      'fetchAllNotes',
      'fetchSubscribedNotes',
      'rehydrateNoteFilters',
      'setNoteFilters',
      'resetNoteFilters'
    ]),

    async fetchNotes () {
      if (this.areFiltersApplied) {
        await this.fetchAllNotes()
      } else {
        await Promise.race([
          this.fetchAllNotes(),
          this.fetchSubscribedNotes()
        ])
      }
    },

    fetchMoreNotes () {
      this.fetchAllNotes({ more: true })
    },

    resetFilters () {
      this.resetNoteFilters()
      this.fetchNotes()
    }
  }
}
</script>
<style lang="scss" scoped>

main.body {

  .section {
    max-width: 1280px;
    margin: auto;
    width: 100%;
    padding: $space-060;

    @include breakpoint(small) {
      padding: $d-med;
    }

    @include tablet {
      padding: $d-large;
    }

    @include desktop {
      padding: $d-xx-large;
    }

    &:nth-of-type(2) {
      padding-top: 0;
    }
  }
}

.load-more {
  display: flex;
  justify-content: center;
  margin: $space-040 0;

  @include desktop {
    margin-top: $space-100;
  }
}

.house-card {
  .image {
    @include lazy-load;

    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
  }

  .details {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    margin-top: $space-050;

    p {
      @include label-xx-sml
    }

    img {
      height: $d-med;
      width: $d-med;
    }
  }
}

::v-deep div.null {
  .image {
    width: 130px;
  }

  .content {
    p {
      @include font('040');
      color: $color_black;
      margin: $space-030 0;
    }

    h3 {
      @include font('050');
      color: $color_black;
      margin-bottom: $space-020;
      text-align: center;
      font-weight: normal;
    }
  }
}

::v-deep main.null {
  @include tablet {
    padding: $d-xx-large 0;
  }
}
</style>
